// src/pages/offlineSales/CustomerImportPage.jsx
import React, { useState, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Download, Upload, CheckCircle, XCircle, AlertCircle, FileSpreadsheet, Trash2, RefreshCw } from 'lucide-react';
import * as XLSX from 'xlsx';
import { useLocalization } from '../../contexts/LocalizationContext';
import { useAlert } from '../../contexts/AlertContext';
import { importCustomers } from '../../services/api/offlineSales/customers';
import { PageHeader, DataTable } from '../../components/common';
import { TEMPLATE_COLUMNS, DATE_KEY, toTemplateRow, downloadCustomerWorkbook } from './customerImportColumns';
import styles from './CustomerImportPage.module.css';

const PREVIEW_LIMIT = 5;

const TEMPLATE_EXAMPLE_ROW = toTemplateRow({
  name: 'Jane Silva', phone: '+94771234567', secondaryPhone: '+94712345678', email: 'jane@example.com',
  address: '123 Main St', city: { name: 'Colombo' }, notes: 'VIP customer', customerType: { label: 'Retail' },
  creditLimit: 50000, creditTermDays: 30, isHardBlock: false, isActive: true,
  openingBalance: { amount: 10000, asOfDate: new Date().toISOString().split('T')[0] },
});

// Excel date cells arrive as serial numbers — convert to YYYY-MM-DD without timezone drift
const cellToString = (key, value) => {
  if (key === DATE_KEY && typeof value === 'number') {
    const d = XLSX.SSF.parse_date_code(value);
    if (d) return `${d.y}-${String(d.m).padStart(2, '0')}-${String(d.d).padStart(2, '0')}`;
  }
  if (key === DATE_KEY) {
    const match = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/.exec(String(value ?? '').trim());
    if (match) return `${match[3]}-${match[2].padStart(2, '0')}-${match[1].padStart(2, '0')}`;
  }
  return String(value ?? '').trim();
};

// ─── Parse file using xlsx ─────────────────────────────────────────────────
const parseFile = (file) => new Promise((resolve, reject) => {
  const reader = new FileReader();
  reader.onload = (e) => {
    try {
      const wb = XLSX.read(e.target.result, { type: 'array' });
      const ws = wb.Sheets[wb.SheetNames[0]];
      const raw = XLSX.utils.sheet_to_json(ws, { header: 1, defval: '' });

      if (raw.length < 2) {
        return reject(new Error('File is empty or has no data rows'));
      }

      // Normalise headers: lowercase + trim
      const headers = raw[0].map(h => String(h).toLowerCase().trim()
        .replace(/\s+/g, '')
        .replace('*', '')
      );

      // Map to expected keys using flexible matching
      const colMap = {};
      TEMPLATE_COLUMNS.forEach(({ key, label }) => {
        const needles = [key, label].map(s => s.toLowerCase().replace(/\s/g, '').replace('*', ''));
        const idx = headers.findIndex(h => needles.includes(h.replace(/\s/g, '')));
        if (idx !== -1) colMap[key] = idx;
      });

      if (colMap.name === undefined && colMap.phone === undefined && colMap.cusCode === undefined) {
        return reject(new Error('Could not find a "Customer Code", "Name" or "Phone" column in the file'));
      }

      const rows = raw.slice(1)
        .map(row => {
          const obj = {};
          TEMPLATE_COLUMNS.forEach(({ key }) => {
            obj[key] = colMap[key] !== undefined ? cellToString(key, row[colMap[key]]) : '';
          });
          return obj;
        })
        .filter(row => row.cusCode || row.name || row.phone); // drop fully empty rows

      if (rows.length === 0) {
        return reject(new Error('No valid data rows found'));
      }

      resolve(rows);
    } catch (err) {
      reject(new Error('Failed to parse file: ' + err.message));
    }
  };
  reader.onerror = () => reject(new Error('Failed to read file'));
  reader.readAsArrayBuffer(file);
});

// ─── Main page ─────────────────────────────────────────────────────────────
const CustomerImportPage = () => {
  const navigate = useNavigate();
  const { t } = useLocalization();
  const { error: showError } = useAlert();

  const [file, setFile] = useState(null);
  const [parsedRows, setParsedRows] = useState(null);
  const [parseError, setParseError] = useState('');
  const [isDragging, setIsDragging] = useState(false);
  const [importing, setImporting] = useState(false);
  const [results, setResults] = useState(null);
  const fileInputRef = useRef(null);

  // ── Template download ──────────────────────────────────────────────────
  const downloadTemplate = () => downloadCustomerWorkbook([TEMPLATE_EXAMPLE_ROW], 'customers_template');

  // ── File handling ──────────────────────────────────────────────────────
  const processFile = useCallback(async (f) => {
    const ext = f.name.split('.').pop().toLowerCase();
    if (!['xlsx', 'xls', 'csv'].includes(ext)) {
      setParseError('Only .xlsx, .xls, or .csv files are supported');
      return;
    }
    setFile(f);
    setParseError('');
    setParsedRows(null);
    setResults(null);
    try {
      const rows = await parseFile(f);
      setParsedRows(rows);
    } catch (err) {
      setParseError(err.message);
      setFile(null);
    }
  }, []);

  const handleFileInput = (e) => {
    if (e.target.files[0]) processFile(e.target.files[0]);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    if (e.dataTransfer.files[0]) processFile(e.dataTransfer.files[0]);
  };

  const handleClear = () => {
    setFile(null);
    setParsedRows(null);
    setParseError('');
    setResults(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  // ── Import ─────────────────────────────────────────────────────────────
  const handleImport = async () => {
    if (!parsedRows || parsedRows.length === 0) return;
    try {
      setImporting(true);
      const result = await importCustomers(parsedRows);
      setResults(result);
    } catch (err) {
      showError(err?.response?.data?.message || 'Import failed');
    } finally {
      setImporting(false);
    }
  };

  const totalRows = parsedRows?.length ?? 0;

  return (
    <div className={styles.page}>
      <PageHeader
        title={t('offlineSales.importCustomers')}
        icon={FileSpreadsheet}
        actions={
          <button className={styles.backBtn} onClick={() => navigate('/offline-sales/customers')}>
            <ArrowLeft size={16} /> {t('offlineSales.backToCustomers')}
          </button>
        }
      />

      {/* ── Step 1: Download template ── */}
      <div className={styles.card}>
        <div className={styles.stepHeader}>
          <span className={styles.stepNum}>1</span>
          <h3>{t('offlineSales.importStep1Title')}</h3>
        </div>
        <p className={styles.stepDesc}>{t('offlineSales.importStep1Desc')}</p>
        <div className={styles.templateColumns}>
          {TEMPLATE_COLUMNS.map(col => (
            <div key={col.key} className={styles.colChip}>
              <span className={styles.colName}>{col.label}</span>
              <span className={styles.colHint}>{col.hint}</span>
            </div>
          ))}
        </div>
        <button className={styles.downloadBtn} onClick={downloadTemplate}>
          <Download size={16} /> {t('offlineSales.downloadTemplate')}
        </button>
      </div>

      {/* ── Step 2: Upload file ── */}
      <div className={styles.card}>
        <div className={styles.stepHeader}>
          <span className={styles.stepNum}>2</span>
          <h3>{t('offlineSales.importStep2Title')}</h3>
        </div>

        {!file ? (
          <div
            className={`${styles.dropZone} ${isDragging ? styles.dragging : ''}`}
            onDragEnter={() => setIsDragging(true)}
            onDragOver={e => { e.preventDefault(); setIsDragging(true); }}
            onDragLeave={() => setIsDragging(false)}
            onDrop={handleDrop}
            onClick={() => fileInputRef.current?.click()}
          >
            <input
              ref={fileInputRef}
              type="file"
              accept=".xlsx,.xls,.csv"
              onChange={handleFileInput}
              className={styles.fileInput}
            />
            <Upload size={32} className={styles.uploadIcon} />
            <p className={styles.dropText}>{t('offlineSales.dropZoneText')}</p>
            <p className={styles.dropSub}>{t('offlineSales.dropZoneSub')}</p>
          </div>
        ) : (
          <div className={styles.fileInfo}>
            <FileSpreadsheet size={20} className={styles.fileIcon} />
            <div className={styles.fileMeta}>
              <span className={styles.fileName}>{file.name}</span>
              {parsedRows && (
                <span className={styles.fileRows}>{totalRows} {t('offlineSales.rowsFound')}</span>
              )}
            </div>
            <button className={styles.clearFileBtn} onClick={handleClear} title="Remove file">
              <Trash2 size={16} />
            </button>
          </div>
        )}

        {parseError && (
          <div className={styles.parseError}>
            <XCircle size={16} />
            {parseError}
          </div>
        )}
      </div>

      {/* ── Step 3: Preview & import ── */}
      {parsedRows && parsedRows.length > 0 && !results && (
        <div className={styles.card}>
          <div className={styles.stepHeader}>
            <span className={styles.stepNum}>3</span>
            <h3>{t('offlineSales.importStep3Title')}</h3>
          </div>
          <p className={styles.stepDesc}>
            {t('offlineSales.previewShowing', { shown: Math.min(PREVIEW_LIMIT, totalRows), total: totalRows })}
          </p>

          <DataTable
            data={parsedRows.slice(0, PREVIEW_LIMIT).map((row, i) => ({ ...row, id: i }))}
            columns={TEMPLATE_COLUMNS.map(col => ({
              key: col.key,
              header: col.label.replace(' *', ''),
              render: (row) => row[col.key] || <span className={styles.empty}>—</span>,
            }))}
          />

          {totalRows > PREVIEW_LIMIT && (
            <p className={styles.moreRows}>
              {t('offlineSales.andMoreRows', { count: totalRows - PREVIEW_LIMIT })}
            </p>
          )}

          <div className={styles.importActions}>
            <button className={styles.cancelImportBtn} onClick={handleClear}>
              {t('common.cancel', 'Cancel')}
            </button>
            <button
              className={styles.importBtn}
              onClick={handleImport}
              disabled={importing}
            >
              {importing
                ? <><span className={styles.spinner} /> {t('offlineSales.importing')}</>
                : <><Upload size={16} /> {t('offlineSales.importNCustomers', { count: totalRows })}</>
              }
            </button>
          </div>
        </div>
      )}

      {/* ── Results ── */}
      {results && (
        <div className={styles.card}>
          <div className={styles.stepHeader}>
            <span className={styles.stepNum}>✓</span>
            <h3>{t('offlineSales.importComplete')}</h3>
          </div>

          <div className={styles.resultStats}>
            <div className={`${styles.statBox} ${styles.statSuccess}`}>
              <CheckCircle size={24} />
              <span className={styles.statNum}>{results.imported}</span>
              <span className={styles.statLabel}>{t('offlineSales.importedCustomers')}</span>
            </div>
            <div className={`${styles.statBox} ${styles.statSuccess}`}>
              <RefreshCw size={24} />
              <span className={styles.statNum}>{results.updated ?? 0}</span>
              <span className={styles.statLabel}>{t('offlineSales.updatedCustomers')}</span>
            </div>
            <div className={`${styles.statBox} ${styles.statSkipped}`}>
              <AlertCircle size={24} />
              <span className={styles.statNum}>{results.skipped}</span>
              <span className={styles.statLabel}>{t('offlineSales.skippedRows')}</span>
            </div>
          </div>

          {results.errors && results.errors.length > 0 && (
            <div className={styles.errorList}>
              <h4>{t('offlineSales.importErrors')}</h4>
              <div className={styles.errorScroll}>
                {results.errors.map((e, i) => (
                  <div key={i} className={styles.errorRow}>
                    <XCircle size={13} className={styles.errorIcon} />
                    <span className={styles.errorRowNum}>{t('offlineSales.rowLabel')} {e.row}</span>
                    {e.name && <span className={styles.errorName}>{e.name}</span>}
                    <span className={styles.errorReason}>{e.reason}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className={styles.importActions}>
            <button className={styles.cancelImportBtn} onClick={handleClear}>
              {t('offlineSales.importAnother')}
            </button>
            <button className={styles.importBtn} onClick={() => navigate('/offline-sales/customers')}>
              {t('offlineSales.backToCustomers')}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default CustomerImportPage;
